'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined';
import Navigation from '../../../components/Navigation';
import {
    DocAnswer,
    DocCode,
    DocEyebrow,
    DocRelated,
    DocSection,
    DocSubtitle,
    DocText,
    DocTitle,
} from '../../../components/DocArticle';
import { encodeBase64 } from '../../../utils/base64';

const failingApplyUrl = (() => {
    const oldEncoded = encodeBase64(JSON.stringify({ items: ['a', 'b'] }, null, 2));
    const patchEncoded = encodeBase64(JSON.stringify([{ op: 'remove', path: '/items/5' }], null, 2));
    return `/?old=${oldEncoded}&patch=${patchEncoded}&mode=apply`;
})();

const errorRows = [
    { symptom: '"path does not exist" on remove or replace', cause: 'The target path is not in the document - a typo, a missing key, or an earlier operation already removed or shifted it.', fix: 'Print the document right before the patch runs and walk the path segment by segment. Remember operations see the document left by previous operations.' },
    { symptom: '"array index out of bounds" (or "index out of range")', cause: 'The index is greater than the last valid position. For add, anything above the array length; for the others, anything above length - 1.', fix: 'Use /- to append without knowing the length, and when removing several elements, remove from the highest index down.' },
    { symptom: '"invalid token" or "bad escape" in a path', cause: 'The path is not a valid JSON Pointer: no leading slash, a leading zero in an array index (/items/01), or a stray ~ not followed by 0 or 1.', fix: 'Escape ~ as ~0 and / as ~1, start every path with /, and write array indexes without leading zeros.' },
    { symptom: 'test operation fails', cause: 'The value at the path is not exactly equal to the test value - often a string/number mismatch ("1" vs 1) or the document changed since the patch was written.', fix: 'Compare types, not just values. A failing test is the patch protecting you - find out why the document differs before forcing it.' },
    { symptom: '"cannot move a value into one of its children"', cause: 'move tries to place a value inside itself, like moving /a to /a/b.', fix: 'Choose a destination outside the source subtree.' },
    { symptom: 'add fails but the path looks right', cause: 'add can create the final step of a path, but every parent segment must already exist. /a/b/c fails when /a/b does not.', fix: 'Create intermediate objects or arrays with earlier add operations.' },
    { symptom: 'Error before any operation runs', cause: 'The patch document itself is malformed: not valid JSON, not an array, an operation missing op or path, or an unknown op.', fix: 'Validate the patch JSON first. The six operations are add, remove, replace, move, copy, test - anything else is rejected.' },
];

export default function JsonPatchErrorsPage() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6 }, px: { xs: 2, sm: 3 } }}>
                    {/* Breadcrumb */}
                    <Box component="nav" sx={{ display: 'flex', mb: 4, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>
                        <Link href="/" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Home</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Link href="/docs" style={{ textDecoration: 'none', color: 'var(--fg-muted)' }}>
                            <Typography component="span" sx={{ '&:hover': { color: 'var(--accent)' }, fontSize: '0.875rem' }}>Documentation</Typography>
                        </Link>
                        <Typography component="span" sx={{ mx: 1, fontSize: '0.875rem', color: 'var(--fg-muted)' }}>/</Typography>
                        <Typography component="span" sx={{ color: 'var(--fg)', fontSize: '0.875rem' }}>JSON Patch Errors</Typography>
                    </Box>

                    <Box sx={{ maxWidth: '800px' }}>
                        <DocEyebrow>Troubleshooting</DocEyebrow>
                        <DocTitle>JSON Patch Errors: Why a Patch Fails</DocTitle>
                        <DocSubtitle>
                            Almost every JSON Patch failure comes down to four causes: the target does not exist, the
                            path is not a valid pointer, an array index is wrong, or a test found the document in an
                            unexpected state. Find your error below.
                        </DocSubtitle>

                        <DocAnswer>
                            A JSON Patch operation fails when its <code>path</code> points at a value that does not
                            exist, the path is not valid{' '}
                            <Link href="/docs/json-pointer" style={{ color: 'var(--accent)' }}>JSON Pointer</Link> syntax,
                            an array index is out of range, or a <code>test</code> value does not match. Evaluation
                            stops at the first failing operation.
                        </DocAnswer>

                        <DocSection>Error lookup table</DocSection>
                        <Box sx={{ overflowX: 'auto', mb: 3, border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
                            <Table size="small" sx={{ minWidth: 640 }}>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: 'var(--bg-subtle)' }}>
                                        <TableCell sx={{ fontWeight: 700, color: 'var(--fg)' }}>Symptom</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: 'var(--fg)' }}>Cause</TableCell>
                                        <TableCell sx={{ fontWeight: 700, color: 'var(--fg)' }}>Fix</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {errorRows.map((row) => (
                                        <TableRow key={row.symptom} sx={{ verticalAlign: 'top' }}>
                                            <TableCell sx={{ color: 'var(--fg)', fontWeight: 600, width: '28%' }}>{row.symptom}</TableCell>
                                            <TableCell sx={{ color: 'var(--fg-muted)' }}>{row.cause}</TableCell>
                                            <TableCell sx={{ color: 'var(--fg-muted)' }}>{row.fix}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>

                        <DocSection>The most common one: array index out of bounds</DocSection>
                        <DocCode label="Document">{`{ "items": ["a", "b"] }`}</DocCode>
                        <DocCode label="Patch - fails">{`[
  { "op": "remove", "path": "/items/5" }
]`}</DocCode>
                        <DocText>
                            The array has two elements, so the only valid indexes are 0 and 1 - index 5 does not exist
                            and the patch fails. Two patterns cause this constantly: indexes that were valid when the
                            patch was written but shifted after earlier operations, and counting from 1 instead of 0.
                            Append with <code>/-</code> instead of a computed index, and remove from the highest index
                            down. Full treatment in{' '}
                            <Link href="/docs/json-patch-array" style={{ color: 'var(--accent)' }}>JSON Patch and arrays</Link>.
                        </DocText>

                        <DocSection>When test fails, it is doing its job</DocSection>
                        <DocText>
                            A <Link href="/operations/test" style={{ color: 'var(--accent)' }}>test operation</Link>{' '}
                            fails the patch by design when reality differs from your assumption - that is optimistic
                            concurrency protecting the document, not a bug in your patch. The one false alarm worth
                            checking first: types. JSON has no implicit conversion, so <code>"1"</code> (string) and{' '}
                            <code>1</code> (number) are different values, and so are <code>true</code> and{' '}
                            <code>"true"</code>.
                        </DocText>

                        <DocSection>A debugging workflow that works</DocSection>
                        <Box component="ol" sx={{ color: 'var(--fg-muted)', lineHeight: 1.8, pl: 3, mb: 3 }}>
                            <li>Paste the document and patch into the <Link href="/" style={{ color: 'var(--accent)' }}>apply or verify tool</Link> - it names the failing operation in plain language instead of a raw library exception.</li>
                            <li>Apply the patch one operation at a time to find the exact step that breaks.</li>
                            <li>Add a <code>test</code> before the failing operation to see what the document actually contains at that path.</li>
                            <li>Check array indexes after every earlier <code>add</code> or <code>remove</code> - they shift.</li>
                        </Box>

                        <DocSection>Reference</DocSection>
                        <DocText>
                            Error conditions per operation are defined throughout{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-4" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6902, section 4</a>,
                            and the rule that evaluation stops at the first error in{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6902#section-5" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>section 5</a>.
                            Path syntax errors come from{' '}
                            <a href="https://datatracker.ietf.org/doc/html/rfc6901#section-3" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>RFC 6901, section 3</a>.
                        </DocText>

                        {/* CTA */}
                        <Box sx={{
                            mt: 6, p: 4, textAlign: 'center',
                            borderRadius: 'var(--radius-lg)',
                            background: 'var(--accent)', color: 'white',
                        }}>
                            <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 2 }}>
                                See a failing patch explained
                            </Typography>
                            <Typography sx={{ mb: 3, opacity: 0.9 }}>
                                Open the apply tool with a patch that fails on an out-of-bounds index - the tool explains the error in plain language.
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                href={failingApplyUrl}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    backgroundColor: 'white', color: 'var(--accent)', fontWeight: 600,
                                    px: 4, py: 1.5, textTransform: 'none',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.9)' },
                                }}
                            >
                                Open the failing example
                            </Button>
                        </Box>

                        <DocRelated links={[
                            { href: '/docs/json-patch-array', title: 'JSON Patch and arrays', description: 'Index shifting and out-of-bounds in depth' },
                            { href: '/docs/json-pointer', title: 'JSON Pointer', description: 'Path syntax and escaping rules' },
                            { href: '/docs/how-to-apply-json-patch', title: 'How to apply a JSON Patch', description: 'Safe application with validation' },
                            { href: '/operations/test', title: 'Test operation', description: 'Failing safely on unexpected state' },
                        ]} />
                    </Box>
                </Container>
            </main>
        </>
    );
}
